import React, {useState, useEffect} from "react";
import {useTheme} from "@mui/material/styles";
import MDBox from "components/MDBox";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  AppBar,
  Card,
  Typography,
  Grid,
  Paper,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery
} from "@mui/material";
import {LastPage, FirstPage, NavigateBefore, NavigateNext} from "@mui/icons-material";
import {GET} from "APIclient";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import * as XLSX from "xlsx";
import {Search as SearchIcon, Download as DownloadIcon} from "@mui/icons-material";
import {Stack, Tooltip} from "@mui/material";
import {GridActionsCellItem} from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useNavigate} from "react-router-dom";
import {POST} from "APIclient";
import axios from "axios";
import {BASE_URL} from "APIclient";
import {formatDesignation} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";

const rowLength = 20;

const getOptions = status => {
  if (status.toLowerCase() === "pending") {
    return ["Extend Probation", "End Probation"];
  }
  return ["Change Notice Period", "Print Confirmation", "Confirmation Letter PDF"];
};

// Custom Pagination Component (unchanged)
function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const handleFirstPage = () => {
    onPageChange(0);
  };

  const handlePreviousPage = () => {
    onPageChange(Math.max(0, page - 1));
  };

  const handleNextPage = () => {
    onPageChange(Math.min(totalPages - 1, page + 1));
  };

  const handleLastPage = () => {
    onGoToLastPage();
  };

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <Typography variant="caption" sx={{color: "#666"}}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>

      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography variant="caption" sx={{mr: 2, color: "#666"}}>
          Page {page + 1} of {totalPages || 1}
        </Typography>

        <Tooltip title="First Page">
          <IconButton onClick={handleFirstPage} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>

        <Tooltip title="Previous Page">
          <IconButton onClick={handlePreviousPage} disabled={page === 0 || loading} size="small">
            <NavigateBefore />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>

        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e"
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function AdminProbationReport() {
  const theme = useTheme();
  const [empId, setEmpId] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setSize] = useState(rowLength);
  const [optionValue, setOptionValue] = React.useState(false);
  const [searchName, setSearchName] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const navigate = useNavigate();

  const columns = [
    {field: "id", headerName: "#", width: 30, align: "left", headerAlign: "left"},
    {field: "empId", headerName: "EMP ID", width: 80, align: "left", headerAlign: "left"},
    {
      field: "empName",
      headerName: "Employee Name",
      width: 200,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "probationStartDate",
      headerName: "Start Date",
      width: 120,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "probationEndDate",
      headerName: "End Date",
      width: 120,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 200,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: params => {
        const status = params.value.toLowerCase();
        let buttonColor;
        let statusText;

        switch (status) {
          case "pending":
            buttonColor = "warning";
            statusText = "Pending";
            break;
          case "completed":
            buttonColor = "success";
            statusText = "Completed";
            break;
        }

        return (
          <MDButton
            variant="contained"
            color={buttonColor}
            size="small"
            disabled
            sx={{
              textTransform: "capitalize",
              fontSize: "0.700rem",
              width: "60px",
              justifyContent: "center"
            }}
          >
            {statusText}
          </MDButton>
        );
      }
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      cellClassName: "actions",
      renderCell: params => {
        const availableOptions = getOptions(params.row.status);

        return (
          <Select
            variant="standard"
            key={`menu-${params.row.empId}`}
            value={optionValue}
            onChange={event => handleView(params.row, event.target.value)}
            displayEmpty
            defaultValue=""
            style={{width: "1px"}}
            renderValue={selected => {
              if (!selected) {
                return (
                  <span style={{display: "flex", alignItems: "center"}}>
                    <MoreVertIcon style={{marginRight: "4px", width: ""}} />
                  </span>
                );
              }
              return selected;
            }}
          >
            <MenuItem value=""></MenuItem>
            {availableOptions.map((option, index) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      }
    }
  ];

  const handleView = async (row, option) => {
    switch (option) {
      case "Change Notice Period":
        navigate(`/changeNoticePeriod/${row.empId}`, {state: {employee: row}});
        break;
      case "Extend Probation":
        navigate(`/empoyeeExtendProbation/${row.empId}`, {state: {employee: row}});
        console.log(`Extending probation for ${row.empId}`);
        break;
      case "End Probation":
        navigate(`/empoyeeEndProbation/${row.empId}`, {state: {employee: row}});
        console.log(`Ending probation for ${row.empId}`);
        break;
      case "Print Confirmation":
        await handlePrintConfirmation(row);
        break;
      case "Confirmation Letter PDF":
        await downloadConfirmationLetter(row);
        break;
      default:
        console.log("Unhandled action:", option);
    }
  };

  const downloadConfirmationLetter = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/printCertificate?type=confirmation_letter&search=${row.empId}`;

      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }
      const fileBlob = new Blob([response.data], {type: "application/pdf"});

      const fileUrl = URL.createObjectURL(fileBlob);

      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `Confirmation_Letter_${row.empId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error printing confirmation:", error);
      alert("Error generating the confirmation letter. Please try again.");
    }
  };

  const handlePrintConfirmation = async row => {
    try {
      const url = `${BASE_URL}/admin/employee/downloadCertificate?type=confirmation_letter&search=${row.empId}`;

      const response = await axios.get(url, {
        headers: {
          Accept: "application/pdf",
          Authorization: `Bearer ${window.sessionStorage.getItem("jwtToken")}`
        },
        responseType: "blob",
        withCredentials: true
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch the PDF");
      }

      const fileBlob = new Blob([response.data], {type: "application/pdf"});

      const fileUrl = URL.createObjectURL(fileBlob);

      const newWindow = window.open(fileUrl, "_blank");
      if (!newWindow) {
        alert("Please allow pop-ups to view the confirmation letter.");
      }
    } catch (error) {
      console.error("Error fetching confirmation letter:", error);
      alert("Error generating the confirmation letter. Please try again.");
    }
  };

  const fetchData = async (
    pageNum = 0,
    pageSz = rowLength,
    empIdVal = "",
    statusVal = "",
    nameVal = ""
  ) => {
    try {
      setLoading(true);

      let backendStatus = "";
      if (statusVal === "pending") backendStatus = 0;
      if (statusVal === "completed") backendStatus = 1;

      const url = `admin/getProbationList?page=${pageNum}&size=${pageSz}&empId=${empIdVal}&status=${backendStatus}&empName=${nameVal}`;

      const response = await GET(url);

      const probationList = response?.data?.data?.paginatedData || [];
      const totalItems = response?.data?.data?.totalItems || 0;
      const totalPg = response?.data?.data?.totalPages || Math.ceil(totalItems / pageSz) || 0;

      const transformedData = probationList.map((item, index) => {
        const formatDate = dateString => {
          if (!dateString) return " - ";
          const date = new Date(dateString);
          return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
          });
        };

        return {
          ...item,
          id: pageNum * pageSz + index + 1,
          empName: `${item.firstName} ${item.lastName}`,
          status: item.probationFlag === 0 ? "Pending" : "Completed",
          probationStartDate: formatDate(item.probationStartDate),
          probationEndDate: formatDate(item.probationEndDate),
          designation: formatDesignation(item?.designation)
        };
      });

      setData(transformedData);
      setFilteredData(transformedData);
      setTotalRows(totalItems);
      setTotalPages(totalPg);
    } catch (error) {
      console.error("Error fetching probation data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const defaultStatus = status || "pending";
    fetchData(page, pageSize, empId, defaultStatus, searchName);
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(0);
    fetchData(0, pageSize, empId, status, searchName);
  };

  const onSearch = e => {
    const {value, name} = e.target;
    if (name === "empId") {
      setEmpId(value || "");
    } else if (name === "searchName") {
      setSearchName(value);
    }
  };

  const handleStatusChange = e => {
    setStatus(e.target.value);
  };

  const handlePaginationModelChange = model => {
    setPage(model.page);
    setSize(model.pageSize);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  const handleEndProbation = async row => {
    const isConfirmed = window.confirm("Are you sure you want to end the probation period?");
    if (isConfirmed) {
      try {
        const url = "admin/endProbation";
        const todayDate = new Date().toISOString().split("T")[0];
        const payload = {
          empId: row.empId,
          probation_end_date: todayDate
        };
        const response = await POST(url, payload);
        if (response?.status === 200) {
          alert("Probation period ended successfully!");
          fetchData(page, pageSize, empId, status, searchName);
        } else {
          alert("Failed to end probation. Try again later.");
        }
      } catch (error) {
        alert("Something went wrong. Please try again!");
      }
    }
  };

  const handleDownloadExcel = () => {
    const exportData = filteredData.map(
      ({empId, empName, probationStartDate, probationEndDate, reason, designation, status}) => ({
        "Employee ID": empId,
        "Employee Name": empName,
        "Start Date": probationStartDate,
        "End Date": probationEndDate,
        Reason: reason || "",
        Designation: designation,
        Status: status
      })
    );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Probation List");
    XLSX.writeFile(workbook, "Probation_List.xlsx");
  };

  const isMobile = useMediaQuery("(max-width: 480px)");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} pb={2} px={3}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(12, 1fr)",
                  gap: 2
                }}
              >
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Probation Report
                </Typography>
                <MDBox sx={{gridColumn: isMobile ? "span 12" : "span 2"}}>
                  <MDInput
                    value={empId}
                    onChange={onSearch}
                    name="empId"
                    label="Employee ID"
                    variant="outlined"
                    fullWidth
                    onKeyDown={e => e.key === "Enter" && handleSearch()}
                  />
                </MDBox>
                <MDBox sx={{gridColumn: isMobile ? "span 12" : "span 2"}}>
                  <MDInput
                    value={searchName}
                    onChange={onSearch}
                    name="searchName"
                    label="Employee Name"
                    variant="outlined"
                    fullWidth
                    onKeyDown={e => e.key === "Enter" && handleSearch()}
                  />
                </MDBox>
                <MDBox sx={{gridColumn: isMobile ? "span 12" : "span 2"}}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={status}
                      onChange={handleStatusChange}
                      label="Status"
                      sx={{height: "43px", display: "flex", alignItems: "center"}}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>
                <MDBox
                  sx={{
                    gridColumn: isMobile ? "span 12" : "span 3",
                    display: "flex",
                    gap: 2,
                    flexDirection: isMobile ? "column" : "row"
                  }}
                >
                  <MDButton
                    variant="contained"
                    color="info"
                    sx={{background: properties.colors.bgcolorbluethem}}
                    onClick={handleSearch}
                    fullWidth={isMobile}
                  >
                    <SearchIcon sx={{mr: 1}} /> Search
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={handleDownloadExcel}
                    fullWidth={isMobile}
                  >
                    <DownloadIcon sx={{mr: 1}} /> Download
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredData}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              paginationModel={{page, pageSize}}
              onPaginationModelChange={handlePaginationModelChange}
              loading={loading}
              components={{Toolbar: GridToolbar}}
              hideFooter
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                fontFamily: properties.fontFamily,
                "& .MuiDataGrid-cell": {
                  textAlign: "left"
                },
                height: "450px",
                "& .MuiDataGrid-scrollbar": {
                  width: "10px !important"
                }
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}
